import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { WifiOff } from "lucide-react";
import { KioskProvider, useKiosk } from "./KioskContext";
import { KioskNav } from "./KioskNav";
import { Attract } from "./Attract";
import { BrandLockup } from "@/components/Wordmark";

const ATTRACT_MS = 60000;
const RESET_MS = 90000;

const useIdle = (onAttract, onReset) => {
  const last = useRef(Date.now());
  const stage = useRef(0);
  useEffect(() => {
    const bump = () => { last.current = Date.now(); stage.current = 0; };
    const evs = ["pointerdown", "keydown", "touchstart", "wheel"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const iv = setInterval(() => {
      const idle = Date.now() - last.current;
      if (idle >= ATTRACT_MS && stage.current === 0) { stage.current = 1; onAttract(); }
      if (idle >= RESET_MS && stage.current === 1) { stage.current = 2; onReset(); }
    }, 1000);
    return () => { clearInterval(iv); evs.forEach((e) => window.removeEventListener(e, bump)); };
  }, [onAttract, onReset]);
};

const UpdateToast = () => {
  const { pulse, t } = useKiosk();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!pulse) return undefined;
    setShow(true);
    const h = setTimeout(() => setShow(false), 3200);
    return () => clearTimeout(h);
  }, [pulse]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="kiosk-update-toast" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#1D1D1F] text-white px-5 py-2.5 text-sm font-medium shadow-lg">
          <span className="h-2 w-2 rounded-full bg-[#5EEAD4]" /> {t("justUpdated")}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const inHours = (h) => {
  if (!h) return true;
  const [hh, mm] = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date()).split(":").map(Number);
  const mins = (hh % 24) * 60 + mm;
  const [oh, om] = h.open.split(":").map(Number);
  const [ch, cm] = h.close.split(":").map(Number);
  return mins >= oh * 60 + om && mins < ch * 60 + cm;
};

const SleepScreen = ({ hours, onWake, t }) => (
  <motion.div data-testid="sleep-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onPointerDown={onWake}
    className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-6 bg-[#1D1D1F] text-center">
    <BrandLockup dark size={48} />
    <p className="font-display text-3xl font-bold tracking-[-0.03em] text-[#F5F5F7]">{t("closed")}</p>
    <p className="text-lg text-[#A1A1A6]">{t("opensAt")} {hours.open}</p>
  </motion.div>
);

function ShellInner() {
  const { data, online, t, resetSession } = useKiosk();
  const [attract, setAttract] = useState(false);
  const navigate = useNavigate();
  const onAttract = useCallback(() => setAttract(true), []);
  const onReset = useCallback(() => { resetSession(); navigate("/kiosk", { replace: true }); }, [resetSession, navigate]);
  useIdle(onAttract, onReset);
  const [awakeUntil, setAwakeUntil] = useState(0);
  const [, tick] = useState(0);
  useEffect(() => { const h = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(h); }, []);
  const hours = data?.showroom?.hours;
  const sleeping = hours && !inHours(hours) && Date.now() > awakeUntil;
  const wake = () => { setAttract(false); navigate("/kiosk", { replace: true }); };

  return (
    <div className="kiosk-root min-h-[100dvh] bg-white">
      <KioskNav />
      {data ? <Outlet /> : <div className="h-[70vh] grid place-items-center"><div className="h-10 w-10 rounded-full border-4 border-[#E5E5EA] border-t-[#0B8577] animate-spin" /></div>}
      {!online && (
        <div data-testid="kiosk-offline-badge" className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full bg-[#F5F5F7] px-4 py-2 text-sm text-[#6E6E73]">
          <WifiOff size={16} /> {t("offline")}
        </div>
      )}
      <UpdateToast />
      <AnimatePresence>{attract && data && !sleeping && <Attract onWake={wake} />}</AnimatePresence>
      <AnimatePresence>{sleeping && <SleepScreen hours={hours} t={t} onWake={() => setAwakeUntil(Date.now() + 180000)} />}</AnimatePresence>
    </div>
  );
}

export default function KioskShell() {
  return (
    <KioskProvider>
      <ShellInner />
    </KioskProvider>
  );
}
